import React, { useCallback, useEffect, useRef, useState } from 'react';
import { toast } from 'react-hot-toast';
import { ChevronDown, Lock } from 'lucide-react';
import {
  Avatar,
  Banner,
  Button,
  ConfirmDialog,
  MoreMenu,
  PanelBody,
  PanelHeader,
  SaveBar,
  SidePanel,
  StatusPill,
  relativeTime,
} from '../kit';
import AccessSection from './AccessSection';
import ProfileSection from './ProfileSection';
import SecuritySection from './SecuritySection';
import ActivitySection from './ActivitySection';
import UsageSection from './usage/UsageSection';
import { PasswordResetDialog, SecretDialog } from './Dialogs';
import {
  apiErrorMessage,
  createUser,
  deleteUserPermanently,
  fetchPbiInventory,
  getUser,
  getUserSecurity,
  isAbortError,
  resendInvite,
  resetUserMfa,
  setUserDeactivated,
  updateUser,
} from './usersApi';
import {
  ACCESS_ERROR_KEYS,
  PROFILE_ERROR_KEYS,
  STATUS_PILL,
  blankOrgEntry,
  buildSavePayload,
  changedReportGroupOrgs,
  countDraftChanges,
  displayName,
  draftFromUser,
  isDeactivated,
  normalizePhone,
  syncLegacyPbiGrants,
  userStatus,
  validateDraft,
} from './userModel';

const PANEL_TABS = [
  { key: 'details', label: 'Details' },
  { key: 'usage', label: 'Usage' },
  { key: 'security', label: 'Security' },
];

/**
 * Edit / invite side panel — one scrolling page (Profile · Organization
 * access · Security · Activity; invite shows the first two) so nothing is
 * hidden behind a tab.
 *   userId null ⇒ invite. `initialUser` (the table row) paints the header
 *   while the fresh doc loads.
 *   onSaved(savedDoc, { isNew }) · onChanged() (status actions) ·
 *   onDeleted(id) · onOpenUser(id) (409 → open existing) · onEmulate(user)
 */
export default function UserPanel({
  userId,
  initialUser,
  directory,
  viewerIsLodgeicAdmin,
  viewerEmail,
  presetOrgCode,
  onClose,
  onSaved,
  onChanged,
  onDeleted,
  onOpenUser,
  onEmulate,
}) {
  const isNew = !userId;
  const [user, setUser] = useState(isNew ? null : initialUser || null);
  const [loadState, setLoadState] = useState({ loading: !isNew, error: null });
  const [baseline, setBaseline] = useState(() => {
    if (!isNew) return draftFromUser(initialUser);
    const d = draftFromUser(null);
    if (presetOrgCode) d.orgAccess = { [presetOrgCode]: blankOrgEntry('') };
    return d;
  });
  const [draft, setDraft] = useState(baseline);
  const [errors, setErrors] = useState({});
  const [focusKey, setFocusKey] = useState(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState(null);
  const [exists, setExists] = useState(null); // { message, id }
  const [security, setSecurity] = useState({ loading: !isNew, data: null, error: null });
  const [dialog, setDialog] = useState(null); // { type, ... }
  const [busy, setBusy] = useState(false);
  const bodyRef = useRef(null);
  // Existing users: Details (profile + organization access) · Usage · Security
  // (sign-in controls + activity trail). An invite is one page, no tabs.
  const [tab, setTab] = useState('details');
  const pickTab = (next) => {
    setTab(next);
    if (bodyRef.current) {
      const scroller = bodyRef.current.querySelector('.ak-panel-body');
      if (scroller) scroller.scrollTop = 0;
    }
  };

  const dirty = countDraftChanges(baseline, draft);
  const touchedPhone = useRef(false);

  // Fresh doc (with adminMeta) + Cognito security facts.
  useEffect(() => {
    if (isNew) return undefined;
    const ctrl = new AbortController();
    getUser(userId, { signal: ctrl.signal })
      .then((doc) => {
        setUser(doc);
        const d = draftFromUser(doc);
        setBaseline(d);
        setDraft(d);
        setLoadState({ loading: false, error: null });
      })
      .catch((err) => {
        if (isAbortError(err)) return;
        setLoadState({ loading: false, error: apiErrorMessage(err, "Couldn't load this user.") });
      });
    getUserSecurity(userId, { signal: ctrl.signal })
      .then((data) => setSecurity({ loading: false, data, error: null }))
      .catch((err) => {
        if (isAbortError(err)) return;
        setSecurity({ loading: false, data: null, error: apiErrorMessage(err, 'Unavailable') });
      });
    return () => ctrl.abort();
    // initialUser only seeds; a new id remounts the panel (keyed by the page)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, isNew]);

  // The sign-in phone lives in Cognito; older docs never stored it. Adopt it
  // as the baseline (unless the admin already typed one).
  const cognitoPhone = security.data?.cognito?.phoneNumber || '';
  useEffect(() => {
    if (!cognitoPhone || loadState.loading) return;
    setBaseline((b) => (normalizePhone(b.phoneNumber) ? b : { ...b, phoneNumber: cognitoPhone }));
    setDraft((d) => (touchedPhone.current || normalizePhone(d.phoneNumber) ? d : { ...d, phoneNumber: cognitoPhone }));
  }, [cognitoPhone, loadState.loading]);

  const trackedSetDraft = useCallback((updater) => {
    setDraft((d) => {
      const next = typeof updater === 'function' ? updater(d) : updater;
      if (next.phoneNumber !== d.phoneNumber) touchedPhone.current = true;
      return next;
    });
    setSaveError(null);
  }, []);

  // Clear a field's error once it's edited.
  useEffect(() => {
    if (!Object.keys(errors).length) return;
    const next = validateDraft(draft, { isNew, manageable: directory.manageable });
    const kept = Object.fromEntries(Object.entries(errors).filter(([k]) => next[k]));
    if (Object.keys(kept).length !== Object.keys(errors).length) setErrors(kept);
  }, [draft]); // eslint-disable-line react-hooks/exhaustive-deps

  // Scroll to + focus the first invalid field.
  useEffect(() => {
    if (!focusKey) return;
    const el = bodyRef.current?.querySelector(`[data-error-key="${String(focusKey).replace(/"/g, '')}"]`);
    if (el) {
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'center', behavior: 'smooth' });
      const target = el.matches('input, select, textarea, button, [tabindex]') ? el : el.querySelector('input, select, button');
      target?.focus({ preventScroll: true });
    }
    setFocusKey(null);
  }, [focusKey]);

  const anyDialogOpen = Boolean(dialog);
  const requestClose = () => {
    if (anyDialogOpen || saving) return;
    if (dirty) setDialog({ type: 'discard' });
    else onClose();
  };

  const status = user ? userStatus(user) : 'active';
  const cognito = security.data?.cognito;
  const invitedPending = !isDeactivated(user) && (status === 'invited' || cognito?.status === 'FORCE_CHANGE_PASSWORD');
  const pill = isDeactivated(user) ? STATUS_PILL.deactivated : invitedPending ? STATUS_PILL.invited : STATUS_PILL.active;

  const save = async () => {
    const errs = validateDraft(draft, { isNew, manageable: directory.manageable });
    if (Object.keys(errs).length) {
      setErrors(errs);
      // Every validated field lives on Details.
      setTab('details');
      // Profile comes first on the page, then access.
      const first = [...PROFILE_ERROR_KEYS(errs), ...ACCESS_ERROR_KEYS(errs)][0];
      if (first) setFocusKey(first);
      return;
    }
    setSaving(true);
    setSaveError(null);
    setExists(null);
    try {
      const payload = buildSavePayload(baseline, draft, { isNew });
      // Orgs whose report groups changed: keep the legacy Power BI grants in step.
      const changed = changedReportGroupOrgs(isNew ? { orgAccess: {} } : baseline, draft);
      if (payload.orgAccess && changed.length) {
        const inventories = await Promise.all(changed.map((code) => fetchPbiInventory(directory.byCode.get(code)?.workspaceId)));
        changed.forEach((code, i) => {
          if (payload.orgAccess[code]) {
            payload.orgAccess[code] = syncLegacyPbiGrants(payload.orgAccess[code], inventories[i].reports, inventories[i].dashboards);
          }
        });
      }
      const saved = isNew ? await createUser(payload) : await updateUser(payload);
      if (isNew) {
        if (saved.cognitoCreated === false) toast.error(`${saved.email} was added, but their sign-in account couldn't be created: ${saved.cognitoError || 'unknown error'}`);
        else if (saved.welcomeEmailSent === false) toast.error(`${saved.email} was added, but the invite email failed — use Resend invite.`);
        else toast.success(`Invite sent to ${saved.email}`);
      } else {
        toast.success('Changes saved');
        if (saved.ignoredOrgs?.length) toast.error(`Access to ${saved.ignoredOrgs.join(', ')} wasn't changed — you aren't a member.`);
      }
      onSaved(saved, { isNew });
    } catch (err) {
      if (err.code === 'USER_EXISTS') {
        setExists({ message: err.message, id: err.existingId });
      } else if (err?.response?.data?.code === 'INVALID_PHONE') {
        setErrors({ phoneNumber: err.response.data.error });
        setFocusKey('phoneNumber');
      } else {
        setSaveError(apiErrorMessage(err, "Couldn't save changes."));
      }
    } finally {
      setSaving(false);
    }
  };

  // ── Header actions ──────────────────────────────────────────────────────
  const refreshUser = (doc) => setUser((u) => ({ ...u, ...doc, adminMeta: u?.adminMeta }));

  const runAction = async (fn) => {
    setBusy(true);
    try {
      await fn();
    } finally {
      setBusy(false);
    }
  };

  const doDeactivate = () => runAction(async () => {
    try {
      const saved = await setUserDeactivated(user.id, true);
      refreshUser(saved);
      toast.success(saved.cognitoDisabled === false ? 'Deactivated, but sign-in could not be disabled' : `${displayName(user)} was deactivated`);
      setDialog(null);
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't deactivate this user"));
    }
  });

  const doRestore = () => runAction(async () => {
    try {
      const saved = await setUserDeactivated(user.id, false);
      refreshUser(saved);
      if (saved.cognitoEnabled && saved.welcomeEmailSent) toast.success('Restored — a new temporary password was emailed');
      else if (saved.cognitoEnabled) toast.error('Restored, but the email failed — use Reset password');
      else toast.error('Restored, but sign-in could not be re-enabled');
      setDialog(null);
      onChanged();
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't restore this user"));
    }
  });

  const doDelete = () => runAction(async () => {
    try {
      const res = await deleteUserPermanently(user.id);
      if (res?.cognitoDeleted === false) toast.error(`Deleted here, but the sign-in account remains: ${res.cognitoDeleteMessage || 'unknown error'}`);
      else toast.success(`${user.email} was deleted`);
      setDialog(null);
      onDeleted(user.id);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't delete this user"));
    }
  });

  const doResetMfa = () => runAction(async () => {
    try {
      const res = await resetUserMfa(user.id);
      toast.success(`MFA reset${res.devicesForgotten ? ` · ${res.devicesForgotten} remembered device${res.devicesForgotten === 1 ? '' : 's'} forgotten` : ''}`);
      setUser((u) => ({ ...u, mfaResetAt: res.mfaResetAt }));
      setDialog(null);
      getUserSecurity(user.id).then((data) => setSecurity({ loading: false, data, error: null })).catch(() => {});
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't reset MFA"));
    }
  });

  const doResend = () => runAction(async () => {
    try {
      const res = await resendInvite(user.id);
      if (res.status === 'sent') {
        toast.success(`Invite re-sent to ${user.email}`);
        if (res.user) refreshUser(res.user);
        onChanged();
      } else if (res.inviteUrl || res.temporaryPassword) {
        setDialog({ type: 'secret', message: res.message, secret: res.inviteUrl || res.temporaryPassword });
        return;
      } else {
        toast.error(res.message || "Couldn't resend the invite");
      }
      setDialog(null);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Couldn't resend the invite"));
    }
  });

  const isViewer = Boolean(user && viewerEmail) && String(user.email).toLowerCase() === String(viewerEmail).toLowerCase();
  const moreItems = user && !isNew ? [
    invitedPending ? { key: 'resend', label: 'Resend invite', onSelect: doResend } : null,
    { key: 'mfa', label: 'Reset MFA', disabled: isDeactivated(user), onSelect: () => setDialog({ type: 'mfa' }) },
    isViewer ? null : isDeactivated(user)
      ? { key: 'restore', label: 'Restore access', onSelect: () => setDialog({ type: 'restore' }) }
      : { key: 'deactivate', label: 'Deactivate', onSelect: () => setDialog({ type: 'deactivate' }) },
    isViewer ? null : { key: 'delete', label: 'Delete permanently', danger: true, separatorBefore: true, onSelect: () => setDialog({ type: 'delete' }) },
  ] : [];

  const lastSignIn = user?.lastSignInAt ? `Last sign-in ${String(relativeTime(user.lastSignInAt)).toLowerCase()}` : invitedPending ? 'Never signed in' : null;
  const mfaLabel = cognito && cognito.exists !== false ? (cognito.mfaEnabled ? 'MFA on' : 'MFA off') : null;
  const title = isNew ? 'Invite user' : displayName(user || initialUser) || 'User';
  const disabled = saving || loadState.loading || Boolean(loadState.error);

  const header = isNew ? (
    <PanelHeader
      title="Invite user"
      sub={<span>They'll get an email with a temporary password to sign in.</span>}
      bordered
      onClose={requestClose}
    />
  ) : (
    <PanelHeader
      avatar={<Avatar name={displayName(user || initialUser)} email={(user || initialUser)?.email} large />}
      title={title}
      badges={user ? <StatusPill tone={pill.tone}>{pill.label}</StatusPill> : null}
      sub={(
        <>
          <Lock size={12} aria-label="Sign-in email (can't be changed)" />
          <span>{(user || initialUser)?.email}</span>
          {lastSignIn ? <span aria-hidden="true">·</span> : null}
          {lastSignIn ? <span>{lastSignIn}</span> : null}
          {mfaLabel ? <span aria-hidden="true">·</span> : null}
          {mfaLabel ? <span>{mfaLabel}</span> : null}
        </>
      )}
      onClose={requestClose}
      actions={user ? (
        <>
          <Button onClick={() => setDialog({ type: 'password' })} disabled={isDeactivated(user)}>Reset password</Button>
          <Button onClick={() => setDialog({ type: 'emulate' })} disabled={isDeactivated(user) || isViewer}>Emulate</Button>
          <span className="ak-foot-spacer" />
          <MoreMenu
            items={moreItems}
            label="More user actions"
            trigger={<button type="button" className="ak-btn">More <ChevronDown aria-hidden="true" /></button>}
          />
        </>
      ) : null}
      tabs={user ? PANEL_TABS : null}
      activeTab={tab}
      onTabChange={pickTab}
      bordered
    />
  );
  const onDetails = isNew || tab === 'details';

  return (
    <>
      <SidePanel open onRequestClose={requestClose} label={title}>
        {header}
        <div className="us-body-wrap" ref={bodyRef}>
          <PanelBody sunken>
            {loadState.error ? <Banner tone="bad" title="Couldn't load this user">{loadState.error}</Banner> : null}
            {exists ? (
              <Banner
                tone="warn"
                title={exists.message}
                action={exists.id ? <Button size="small" onClick={() => onOpenUser(exists.id)}>Open existing user</Button> : null}
              >
                {exists.id ? 'Open their record to change their access instead.' : null}
              </Banner>
            ) : null}
            {saveError ? <Banner tone="bad" title="Couldn't save">{saveError}</Banner> : null}
            {loadState.loading && !user ? <div className="ak-muted us-pad">Loading…</div> : null}

            {(!loadState.loading || user) && onDetails ? (
              <>
                <ProfileSection
                  key={loadState.loading ? 'loading' : 'ready'}
                  draft={draft}
                  setDraft={trackedSetDraft}
                  errors={errors}
                  isNew={isNew}
                  disabled={disabled}
                  showAdminSwitch={viewerIsLodgeicAdmin}
                />
                <AccessSection user={user} draft={draft} setDraft={trackedSetDraft} directory={directory} errors={errors} disabled={disabled} />
              </>
            ) : null}

            {!isNew && user && tab === 'usage' ? <UsageSection user={user} directory={directory} /> : null}

            {!isNew && user && tab === 'security' ? (
              <>
                    <SecuritySection
                      user={user}
                      security={security}
                      canResendInvite={invitedPending}
                      isViewer={isViewer}
                      onResetPassword={() => setDialog({ type: 'password' })}
                      onResetMfa={() => setDialog({ type: 'mfa' })}
                      onDeactivate={() => setDialog({ type: 'deactivate' })}
                      onRestore={() => setDialog({ type: 'restore' })}
                      onResendInvite={doResend}
                    />
                    <ActivitySection user={user} />
              </>
            ) : null}
          </PanelBody>
        </div>
        {onDetails || dirty ? (
        <SaveBar
          dirtyCount={dirty}
          saving={saving}
          disabled={disabled}
          saveLabel={isNew ? 'Send invite' : 'Save changes'}
          left={isNew && !dirty ? <span className="ak-muted" style={{ fontSize: 13 }}>Needs a name, email and at least one organization.</span> : undefined}
          onDiscard={() => (dirty ? setDialog({ type: 'discard' }) : onClose())}
          onSave={save}
        />
        ) : null}
      </SidePanel>

      <ConfirmDialog
        open={dialog?.type === 'discard'}
        title="Discard changes?"
        confirmLabel="Discard"
        danger
        onCancel={() => setDialog(null)}
        onConfirm={() => { setDialog(null); onClose(); }}
      >
        <p>You have {dirty} unsaved change{dirty === 1 ? '' : 's'}{isNew ? ' to this invite' : ` for ${title}`}. They'll be lost.</p>
      </ConfirmDialog>

      <PasswordResetDialog open={dialog?.type === 'password'} user={user} onClose={() => setDialog(null)} />

      <ConfirmDialog
        open={dialog?.type === 'mfa'}
        title="Reset MFA?"
        confirmLabel="Reset MFA"
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={doResetMfa}
      >
        <p>Turns off {user?.firstName || 'their'}'s two-factor methods and forgets remembered devices. If their organization requires MFA they'll set it up again at next sign-in.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog?.type === 'emulate'}
        title={`Emulate ${displayName(user)}?`}
        confirmLabel="Start emulating"
        onCancel={() => setDialog(null)}
        onConfirm={() => { setDialog(null); onEmulate(user); }}
      >
        <p>You'll browse the app as {user?.email} until you stop emulating.{dirty ? ' Your unsaved changes here will be lost.' : ''}</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog?.type === 'deactivate'}
        title={`Deactivate ${displayName(user)}?`}
        confirmLabel="Deactivate"
        danger
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={doDeactivate}
      >
        <p>They're signed out of Lodgeic and can't sign in. Their access and settings are kept, so you can restore them later.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog?.type === 'restore'}
        title={`Restore ${displayName(user)}?`}
        confirmLabel="Restore access"
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={doRestore}
      >
        <p>Re-enables their sign-in and emails them a new temporary password.</p>
      </ConfirmDialog>

      <ConfirmDialog
        open={dialog?.type === 'delete'}
        title="Delete permanently?"
        confirmLabel="Delete permanently"
        danger
        busy={busy}
        onCancel={() => setDialog(null)}
        onConfirm={doDelete}
      >
        <p>Removes {user?.email}'s record, sign-in account, report subscriptions and alerts. This can't be undone — deactivate instead if they might come back.</p>
      </ConfirmDialog>

      <SecretDialog
        open={dialog?.type === 'secret'}
        title="Invite email failed"
        message={dialog?.message}
        secret={dialog?.secret}
        onClose={() => setDialog(null)}
      />
    </>
  );
}

